import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  Checkbox,
  DataTable,
  Divider,
  FormLayout,
  InlineGrid,
  InlineStack,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import {
  useCartsOverview,
  useCreateSchedule,
  useUpdateTriggerSchedule,
  type CartOverviewItem,
  type CartOverviewLine,
  type DeviceGroup,
  type NotificationSchedule,
} from '@/queries/notifications';
import { formatTime } from '../shared/useNotificationFormState';

type CartKind = 'cart_idle' | 'price_drop';

interface KindCopy {
  heading: string;
  description: string;
  placeholders: string;
  defaults: { title: string; body: string; params: Record<string, number> };
}

const COPY: Record<CartKind, KindCopy> = {
  cart_idle: {
    heading: 'الكارت مستنيك',
    description:
      'لما عميل يسيب منتجات في الكارت ومايشتريش. بيتبعت مرة واحدة لكل كارت، ومش بيتبعت لو اشترى أو فضّى الكارت.',
    placeholders: '{product} اسم أول منتج · {count} عدد القطع · {total} الإجمالي',
    defaults: {
      title: 'Your cart is waiting 🎾',
      body: '{product} is still in your cart. Complete your order before it sells out.',
      params: { hours: 3, max_age_hours: 72, quiet_start: 23, quiet_end: 9, cooldown_hours: 12 },
    },
  },
  price_drop: {
    heading: 'منتج نزل سعره (الكارت أو المفضلة)',
    description:
      'بيقارن سعر المنتجات اللي في الكارت أو المفضلة بسعرها وقت ما العميل ضافها. بيتبعت مرة لكل مستوى سعر، والضغط عليه يفتح المنتج. لو المنتج في الاتنين، بيتبعت إشعار واحد بس.',
    placeholders:
      '{product} اسم المنتج · {old_price} السعر القديم · {new_price} السعر الجديد · {percent} نسبة الخصم · {where} "in your cart" أو "in your wishlist"',
    defaults: {
      title: 'Price drop on {product} 🔥',
      body: 'Now EGP {new_price} instead of {old_price} ({percent}% off). Still {where}.',
      params: {
        min_pct: 5,
        check_every_min: 30,
        max_age_days: 30,
        quiet_start: 23,
        quiet_end: 9,
        cooldown_hours: 12,
        watch_cart: 1,
        watch_wishlist: 1,
      },
    },
  },
};

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
  label: `${String(h).padStart(2, '0')}:00`,
  value: String(h),
}));

export interface CartsTabProps {
  schedules: NotificationSchedule[];
  groups: DeviceGroup[];
}

export function CartsTab({ schedules, groups }: CartsTabProps) {
  const byKind = useMemo(() => {
    const out: Partial<Record<CartKind, NotificationSchedule>> = {};
    for (const s of schedules) {
      if (s.schedule_type !== 'trigger') continue;
      if (s.trigger_kind === 'cart_idle' || s.trigger_kind === 'price_drop') {
        // Newest wins when several exist (list is created_at DESC).
        out[s.trigger_kind] ??= s;
      }
    }
    return out;
  }, [schedules]);

  const idleHours = Number(byKind.cart_idle?.trigger_params?.hours ?? 3) || 3;

  return (
    <BlockStack gap="400">
      <CartStats idleHours={idleHours} />
      <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
        <AutomationCard kind="cart_idle" schedule={byKind.cart_idle} groups={groups} />
        <AutomationCard kind="price_drop" schedule={byKind.price_drop} groups={groups} />
      </InlineGrid>
      <CartsTable idleHours={idleHours} />
    </BlockStack>
  );
}

// ---------------------------------------------------------------------------
// Automation settings
// ---------------------------------------------------------------------------

function num(params: Record<string, unknown> | null | undefined, key: string, fallback: number): string {
  const v = params?.[key];
  return v === undefined || v === null || v === '' ? String(fallback) : String(v);
}

function AutomationCard({
  kind,
  schedule,
  groups,
}: {
  kind: CartKind;
  schedule: NotificationSchedule | undefined;
  groups: DeviceGroup[];
}) {
  const copy = COPY[kind];
  const d = copy.defaults;
  const create = useCreateSchedule();
  const update = useUpdateTriggerSchedule();

  const [title, setTitle] = useState(d.title);
  const [body, setBody] = useState(d.body);
  const [p, setP] = useState<Record<string, string>>({});
  const [groupId, setGroupId] = useState('');
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Load the stored schedule into the form whenever it changes on the server.
  useEffect(() => {
    const params = schedule?.trigger_params ?? null;
    setTitle(schedule?.title ?? d.title);
    setBody(schedule?.body ?? d.body);
    const next: Record<string, string> = {};
    for (const [k, v] of Object.entries(d.params)) next[k] = num(params, k, v);
    setP(next);
    setGroupId(params?.test_group_id ? String(params.test_group_id) : '');
  }, [schedule, d]);

  const setParam = (key: string) => (v: string) => {
    setSaved(false);
    setP((prev) => ({ ...prev, [key]: v }));
  };

  const buildParams = (): Record<string, number | null> => {
    const out: Record<string, number | null> = {};
    for (const [k, v] of Object.entries(p)) out[k] = v === '' ? null : Number(v);
    out.test_group_id = groupId ? Number(groupId) : null;
    return out;
  };

  const onSave = async () => {
    setErr(null);
    if (!title.trim() || !body.trim()) {
      setErr('العنوان والنص مطلوبين');
      return;
    }
    try {
      if (schedule) {
        await update.mutateAsync({
          id: schedule.id,
          title,
          body,
          trigger_params: buildParams(),
        });
      } else {
        // Create switched OFF: the owner enables it deliberately after
        // checking the copy (and ideally a test group).
        const created = await create.mutateAsync({
          title,
          body,
          schedule_type: 'trigger',
          trigger_kind: kind,
          trigger_params: buildParams(),
          target: { type: 'none', value: '' },
        });
        await update.mutateAsync({ id: created.id, enabled: false });
      }
      setSaved(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'حصل خطأ في الحفظ');
    }
  };

  const onToggle = async () => {
    if (!schedule) return;
    setErr(null);
    try {
      await update.mutateAsync({ id: schedule.id, enabled: !schedule.enabled });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'حصل خطأ');
    }
  };

  const groupOptions = [
    { label: 'كل العملاء', value: '' },
    ...groups.map((g) => ({ label: `جروب تجربة: ${g.name} (${g.member_count})`, value: String(g.id) })),
  ];
  const busy = create.isPending || update.isPending;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <InlineStack gap="200" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {copy.heading}
            </Text>
            {schedule ? (
              <Badge tone={schedule.enabled ? 'success' : undefined}>
                {schedule.enabled ? 'شغّال' : 'موقّف'}
              </Badge>
            ) : (
              <Badge>مش متفعّل</Badge>
            )}
            {schedule && groupId ? <Badge tone="attention">تجربة فقط</Badge> : null}
          </InlineStack>
          {schedule ? (
            <Button onClick={onToggle} loading={update.isPending} variant={schedule.enabled ? 'secondary' : 'primary'}>
              {schedule.enabled ? 'إيقاف' : 'تشغيل'}
            </Button>
          ) : null}
        </InlineStack>

        <Text as="p" tone="subdued">
          {copy.description}
        </Text>

        {err ? (
          <Banner tone="critical" onDismiss={() => setErr(null)}>
            {err}
          </Banner>
        ) : null}
        {saved ? (
          <Banner tone="success" onDismiss={() => setSaved(false)}>
            {schedule ? 'اتحفظ' : 'اتعمل وهو موقّف. دوس تشغيل لما تكون جاهز.'}
          </Banner>
        ) : null}

        <FormLayout>
          <TextField label="العنوان" value={title} onChange={(v) => { setSaved(false); setTitle(v); }} autoComplete="off" />
          <TextField
            label="النص"
            value={body}
            onChange={(v) => { setSaved(false); setBody(v); }}
            multiline={2}
            autoComplete="off"
            helpText={`متغيرات: ${copy.placeholders}`}
          />

          {kind === 'cart_idle' ? (
            <FormLayout.Group condensed>
              <TextField label="يتبعت بعد (ساعة)" type="number" min={1} value={p.hours ?? ''} onChange={setParam('hours')} autoComplete="off" />
              <TextField
                label="تجاهل الكارتات الأقدم من (ساعة)"
                type="number"
                min={1}
                value={p.max_age_hours ?? ''}
                onChange={setParam('max_age_hours')}
                autoComplete="off"
              />
            </FormLayout.Group>
          ) : (
            <>
              <InlineStack gap="400">
                <Checkbox
                  label="راقب الكارت"
                  checked={p.watch_cart !== '0'}
                  onChange={(v) => setParam('watch_cart')(v ? '1' : '0')}
                />
                <Checkbox
                  label="راقب المفضلة"
                  checked={p.watch_wishlist !== '0'}
                  onChange={(v) => setParam('watch_wishlist')(v ? '1' : '0')}
                />
              </InlineStack>
              <FormLayout.Group condensed>
              <TextField label="أقل نسبة خصم (%)" type="number" min={1} value={p.min_pct ?? ''} onChange={setParam('min_pct')} autoComplete="off" />
              <TextField
                label="افحص الأسعار كل (دقيقة)"
                type="number"
                min={5}
                value={p.check_every_min ?? ''}
                onChange={setParam('check_every_min')}
                autoComplete="off"
              />
              <TextField
                label="تجاهل المنتجات المضافة من أكتر من (يوم)"
                type="number"
                min={1}
                value={p.max_age_days ?? ''}
                onChange={setParam('max_age_days')}
                autoComplete="off"
              />
              </FormLayout.Group>
            </>
          )}

          <FormLayout.Group condensed>
            <Select label="ممنوع الإرسال من (توقيت القاهرة)" options={HOUR_OPTIONS} value={p.quiet_start ?? '23'} onChange={setParam('quiet_start')} />
            <Select label="لحد" options={HOUR_OPTIONS} value={p.quiet_end ?? '9'} onChange={setParam('quiet_end')} />
            <TextField
              label="أقل مدة بين إشعارين (ساعة)"
              type="number"
              min={0}
              value={p.cooldown_hours ?? ''}
              onChange={setParam('cooldown_hours')}
              autoComplete="off"
              helpText="لنفس العميل، بين الإشعارين دول"
            />
          </FormLayout.Group>

          <Select
            label="يتبعت لمين"
            options={groupOptions}
            value={groupId}
            onChange={(v) => { setSaved(false); setGroupId(v); }}
            helpText="اختار جروب تجربة عشان تجرب على موبايلك الأول قبل ما تفتحه للكل"
          />
        </FormLayout>

        <InlineStack align="space-between" blockAlign="center">
          <Text as="span" variant="bodySm" tone="subdued">
            {schedule
              ? `اتبعت ${schedule.fire_count} مرة${schedule.last_fired_at ? ` · آخر مرة ${formatTime(schedule.last_fired_at)}` : ''}`
              : 'لسه ماتعملش'}
          </Text>
          <Button variant="primary" onClick={onSave} loading={busy}>
            {schedule ? 'حفظ' : 'إنشاء (موقّف)'}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Stats + table
// ---------------------------------------------------------------------------

function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${Math.round(v).toLocaleString('en-US')} ج.م`;
}

function CartStats({ idleHours }: { idleHours: number }) {
  const q = useCartsOverview(idleHours);
  const s = q.data?.summary;
  const tiles: { label: string; value: string }[] = [
    { label: 'كارتات مفتوحة', value: s ? String(s.active) : '—' },
    { label: `متسابة أكتر من ${idleHours} ساعة`, value: s ? String(s.idle) : '—' },
    { label: 'قيمة الكارتات المفتوحة', value: s ? money(s.open_value) : '—' },
    { label: 'اتشرت', value: s ? String(s.purchased) : '—' },
    { label: 'اتفضّت', value: s ? String(s.emptied) : '—' },
    {
      label: 'إشعارات آخر 30 يوم',
      value: s ? `${s.sent_30d.cart_idle ?? 0} كارت · ${s.sent_30d.price_drop ?? 0} خصم` : '—',
    },
  ];
  return (
    <InlineGrid columns={{ xs: 2, md: 6 }} gap="300">
      {tiles.map((t) => (
        <Card key={t.label}>
          <BlockStack gap="100">
            <Text as="span" variant="bodySm" tone="subdued">
              {t.label}
            </Text>
            <Text as="span" variant="headingLg">
              {q.isLoading ? <Spinner size="small" /> : t.value}
            </Text>
          </BlockStack>
        </Card>
      ))}
    </InlineGrid>
  );
}

const STATUS_LABEL: Record<CartOverviewItem['status'], { label: string; tone?: 'success' | 'attention' | 'info' }> = {
  active: { label: 'مفتوح', tone: 'attention' },
  purchased: { label: 'اتشرى', tone: 'success' },
  empty: { label: 'اتفضّى' },
};

function CartsTable({ idleHours }: { idleHours: number }) {
  const q = useCartsOverview(idleHours);
  const [filter, setFilter] = useState<'active' | 'all'>('active');

  const all: CartOverviewItem[] = q.data?.items ?? [];
  const items = all.filter((c) => filter === 'all' || c.status === 'active');
  const rows = items.map((c: CartOverviewItem) => [
    <Badge key="s" tone={STATUS_LABEL[c.status].tone}>
      {STATUS_LABEL[c.status].label}
    </Badge>,
    <BlockStack key="l" gap="050">
      {c.lines.slice(0, 3).map((l: CartOverviewLine, i: number) => (
        <Text key={i} as="span" variant="bodySm">
          {l.quantity}× {l.title || l.handle || '—'}
          {l.variant && l.variant !== 'Default Title' ? ` (${l.variant})` : ''}
        </Text>
      ))}
      {c.lines.length > 3 ? (
        <Text as="span" variant="bodySm" tone="subdued">
          +{c.lines.length - 3} كمان
        </Text>
      ) : null}
      {!c.lines.length ? (
        <Text as="span" variant="bodySm" tone="subdued">
          —
        </Text>
      ) : null}
    </BlockStack>,
    money(c.subtotal),
    c.customer_id ? `عميل ${c.customer_id}` : 'زائر',
    c.platform,
    formatTime(c.updated_at),
    c.last_push_at ? formatTime(c.last_push_at) : '—',
  ]);

  return (
    <Card padding="0">
      <Box padding="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h2" variant="headingMd">
            الكارتات
          </Text>
          <InlineStack gap="200">
            <Button pressed={filter === 'active'} onClick={() => setFilter('active')}>
              المفتوحة
            </Button>
            <Button pressed={filter === 'all'} onClick={() => setFilter('all')}>
              الكل
            </Button>
            <Button onClick={() => q.refetch()} loading={q.isFetching}>
              تحديث
            </Button>
          </InlineStack>
        </InlineStack>
      </Box>
      <Divider />
      {q.isLoading ? (
        <Box padding="400">
          <InlineStack align="center">
            <Spinner accessibilityLabel="loading" />
          </InlineStack>
        </Box>
      ) : q.error ? (
        <Box padding="400">
          <Banner tone="critical">مقدرناش نجيب الكارتات</Banner>
        </Box>
      ) : rows.length ? (
        <DataTable
          columnContentTypes={['text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
          headings={['الحالة', 'المنتجات', 'الإجمالي', 'العميل', 'الجهاز', 'آخر تعديل', 'آخر إشعار']}
          rows={rows}
        />
      ) : (
        <Box padding="400">
          <Text as="p" tone="subdued">
            مفيش كارتات لسه. هتظهر هنا أول ما العملاء ينزلوا نسخة التطبيق الجديدة ويضيفوا منتجات.
          </Text>
        </Box>
      )}
    </Card>
  );
}
